import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatToLocalDateTime } from '../../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  RefreshCw,
  Radio,
  User,
  Scan,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Loader2,
  Users,
  QrCode
} from 'lucide-react';

const MobileLiveDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // All Scans tab
  const [recentScans, setRecentScans] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Person-wise tab
  const [personSearch, setPersonSearch] = useState('');
  const [allAttendees, setAllAttendees] = useState([]);
  const [attendeeScanCounts, setAttendeeScanCounts] = useState({});
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personScans, setPersonScans] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  
  // Scanner-wise tab - now shows all scanners by default
  const [scannerStats, setScannerStats] = useState([]);
  const [selectedScanner, setSelectedScanner] = useState(null);
  const [scannerAttendees, setScannerAttendees] = useState([]);
  
  const refreshIntervalRef = useRef(null);

  // Fetch event and dashboards
  const fetchInitialData = useCallback(async () => {
    try {
      const [eventRes, dashboardsRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/dashboards`)
      ]);
      setEvent(eventRes.data);
      setDashboards(dashboardsRes.data);
    } catch (error) {
      toast.error('Failed to load event data');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch recent scans for "All Scans" tab
  const fetchRecentScans = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await api.get(`/events/${eventId}/scan-logs`, {
        params: { limit: 50, page: 1 }
      });
      setRecentScans(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch recent scans:', error);
    } finally {
      setRefreshing(false);
    }
  }, [eventId]);

  // Fetch all attendees with their scan counts for Person tab
  const fetchAllAttendees = useCallback(async () => {
    setLoadingAttendees(true);
    try {
      // Fetch attendees (paginated, get first 100)
      const attendeesRes = await api.get(`/events/${eventId}/attendees`, {
        params: { limit: 100, page: 1 }
      });
      const attendees = attendeesRes.data.attendees || [];
      setAllAttendees(attendees);
      
      // Fetch all scan logs to calculate scan counts per attendee (limit to 500 per API)
      const scansRes = await api.get(`/events/${eventId}/scan-logs`, {
        params: { limit: 500 }
      });
      const scans = scansRes.data.logs || [];
      
      // Filter only success scans client-side
      const successScans = scans.filter(s => s.scan_status === 'success');
      
      // Calculate scan counts per attendee
      const counts = {};
      successScans.forEach(scan => {
        const aid = scan.attendee_id;
        if (!counts[aid]) {
          counts[aid] = { total: 0, scanners: {} };
        }
        counts[aid].total++;
        if (!counts[aid].scanners[scan.dashboard_id]) {
          counts[aid].scanners[scan.dashboard_id] = 0;
        }
        counts[aid].scanners[scan.dashboard_id]++;
      });
      setAttendeeScanCounts(counts);
    } catch (error) {
      console.error('Failed to fetch attendees:', error);
    } finally {
      setLoadingAttendees(false);
    }
  }, [eventId]);

  // Fetch scanner stats for Scanner tab (shows all scanners with counts)
  const fetchScannerStats = useCallback(async (dashboardsList) => {
    const currentDashboards = dashboardsList || dashboards;
    if (!currentDashboards.length) return;
    
    try {
      const scansRes = await api.get(`/events/${eventId}/scan-logs`, {
        params: { limit: 500 }
      });
      const scans = scansRes.data.logs || [];
      
      // Filter only success scans client-side
      const successScans = scans.filter(s => s.scan_status === 'success');
      
      // Calculate stats for each scanner/dashboard
      const statsMap = {};
      currentDashboards.forEach(d => {
        statsMap[d.dashboard_id] = {
          dashboard_id: d.dashboard_id,
          dashboard_name: d.dashboard_name,
          dashboard_type: d.dashboard_type,
          total_scans: 0,
          unique_attendees: new Set()
        };
      });
      
      successScans.forEach(scan => {
        if (statsMap[scan.dashboard_id]) {
          statsMap[scan.dashboard_id].total_scans++;
          statsMap[scan.dashboard_id].unique_attendees.add(scan.attendee_id);
        }
      });
      
      // Convert to array and finalize unique counts
      const statsArray = Object.values(statsMap).map(s => ({
        ...s,
        unique_attendees: s.unique_attendees.size
      }));
      
      setScannerStats(statsArray);
    } catch (error) {
      console.error('Failed to fetch scanner stats:', error);
    }
  }, [eventId, dashboards]);

  // Fetch person's scan history
  const fetchPersonScans = async (attendee) => {
    setSelectedPerson(attendee);
    
    try {
      // Fetch scans specifically for this attendee (using server-side filter)
      const response = await api.get(`/events/${eventId}/scan-logs`, {
        params: { attendee_id: attendee.attendee_id, limit: 500 }
      });
      
      const personSpecificScans = response.data.logs || [];
      
      // Group by dashboard
      const scansByDashboard = {};
      dashboards.forEach(d => {
        scansByDashboard[d.dashboard_id] = {
          dashboard_id: d.dashboard_id,
          dashboard_name: d.dashboard_name,
          scans: []
        };
      });
      
      personSpecificScans.forEach(scan => {
        if (scan.scan_status === 'success' && scansByDashboard[scan.dashboard_id]) {
          scansByDashboard[scan.dashboard_id].scans.push(scan);
        }
      });
      
      setPersonScans(Object.values(scansByDashboard));
    } catch (error) {
      toast.error('Failed to fetch scan history');
    }
  };

  // Fetch attendees for a specific scanner
  const fetchScannerAttendees = async (scanner) => {
    setSelectedScanner(scanner);
    
    try {
      const response = await api.get(`/events/${eventId}/scan-logs`, {
        params: { dashboard_id: scanner.dashboard_id, status: 'success', limit: 500 }
      });
      
      const scans = response.data.logs || [];
      
      // Group by attendee and count scans
      const attendeeMap = {};
      scans.forEach(scan => {
        const aid = scan.attendee_id;
        if (!attendeeMap[aid]) {
          attendeeMap[aid] = {
            attendee_id: aid,
            name: scan.attendee_name,
            ticket_id: scan.ticket_id,
            scan_count: 0,
            scan_times: []
          };
        }
        attendeeMap[aid].scan_count++;
        attendeeMap[aid].scan_times.push(scan.scanned_at);
      });
      
      const attendees = Object.values(attendeeMap).sort((a, b) => b.scan_count - a.scan_count);
      setScannerAttendees(attendees);
    } catch (error) {
      toast.error('Failed to fetch scanner data');
    }
  };

  // Filter attendees based on search
  const filteredAttendees = useMemo(() => {
    if (!personSearch.trim()) return allAttendees;
    const search = personSearch.toLowerCase();
    return allAttendees.filter(a => 
      a.name?.toLowerCase().includes(search) || 
      a.ticket_id?.toLowerCase().includes(search)
    );
  }, [allAttendees, personSearch]);

  useEffect(() => {
    fetchInitialData();
    fetchRecentScans();
  }, [fetchInitialData, fetchRecentScans]);

  // Fetch attendees when tab data is needed
  useEffect(() => {
    if (dashboards.length > 0) {
      fetchAllAttendees();
      fetchScannerStats(dashboards);
    }
  }, [dashboards, fetchAllAttendees, fetchScannerStats]);

  // Auto-refresh for All Scans
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(fetchRecentScans, 5000);
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, fetchRecentScans]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile optimized */}
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold truncate max-w-[200px]">{event?.event_name}</h1>
              <p className="text-xs text-gray-500">Live Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {autoRefresh && (
              <Badge variant="outline" className="text-green-600 border-green-300" data-testid="live-badge">
                <Radio className="h-3 w-3 mr-1 animate-pulse" /> LIVE
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        <Tabs defaultValue="all-scans" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="all-scans" className="text-xs sm:text-sm" data-testid="tab-all-scans">
              <Radio className="h-3 w-3 mr-1 sm:mr-2" />
              All Scans
            </TabsTrigger>
            <TabsTrigger value="person-wise" className="text-xs sm:text-sm" data-testid="tab-person">
              <User className="h-3 w-3 mr-1 sm:mr-2" />
              Person
            </TabsTrigger>
            <TabsTrigger value="scanner-wise" className="text-xs sm:text-sm" data-testid="tab-scanner">
              <Scan className="h-3 w-3 mr-1 sm:mr-2" />
              Scanner
            </TabsTrigger>
          </TabsList>

          {/* All Scans Tab */}
          <TabsContent value="all-scans">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Recent Scans</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      className={autoRefresh ? 'text-green-600' : 'text-gray-400'}
                      data-testid="auto-refresh-toggle"
                    >
                      {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchRecentScans}
                      disabled={refreshing}
                      data-testid="refresh-button"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="max-h-[60vh] overflow-y-auto">
                {recentScans.length === 0 ? (
                  <p className="text-center text-gray-500 py-8" data-testid="no-scans-message">No scans yet</p>
                ) : (
                  <div className="space-y-2">
                    {recentScans.map((scan) => (
                      <div
                        key={scan.scan_id}
                        className={`p-3 rounded-lg border ${
                          scan.scan_status === 'success'
                            ? 'bg-green-50 border-green-200'
                            : scan.scan_status === 'duplicate'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                        data-testid={`scan-item-${scan.scan_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm">{scan.attendee_name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{scan.ticket_id}</p>
                          </div>
                          <div className="text-right">
                            <Badge
                              variant={scan.scan_status === 'success' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {scan.scan_status}
                            </Badge>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>{scan.dashboard_name}</span>
                          <span>{formatToLocalDateTime(scan.scanned_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Person-wise Tab */}
          <TabsContent value="person-wise">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {selectedPerson ? 'Scan History' : 'Attendees'}
                  </CardTitle>
                  {selectedPerson && (
                    <Button variant="outline" size="sm" onClick={() => setSelectedPerson(null)} data-testid="clear-person">
                      Back to List
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedPerson ? (
                  <>
                    {/* Search Bar */}
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search by Name or Ticket ID..."
                          value={personSearch}
                          onChange={(e) => setPersonSearch(e.target.value)}
                          className="pl-10"
                          data-testid="person-search-input"
                        />
                      </div>
                    </div>
                    
                    {/* Attendees List */}
                    {loadingAttendees ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : filteredAttendees.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No attendees found</p>
                    ) : (
                      <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                        {filteredAttendees.map((attendee) => {
                          const scanInfo = attendeeScanCounts[attendee.attendee_id];
                          const totalScans = scanInfo?.total || 0;
                          const scannedLocations = scanInfo ? Object.keys(scanInfo.scanners).length : 0;
                          
                          return (
                            <div
                              key={attendee.attendee_id}
                              className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() => fetchPersonScans(attendee)}
                              data-testid={`attendee-item-${attendee.attendee_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{attendee.name}</p>
                                  <p className="text-xs text-gray-500">{attendee.ticket_id} • {attendee.category_name}</p>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                  {totalScans > 0 ? (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      {scannedLocations}/{dashboards.length}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400 text-xs">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      0/{dashboards.length}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Selected Person Header */}
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <h3 className="font-bold">{selectedPerson.name}</h3>
                      <p className="text-sm text-gray-600">{selectedPerson.ticket_id} • {selectedPerson.category_name}</p>
                    </div>
                    
                    {/* Scan History by Location */}
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                      {personScans.map((scanner) => (
                        <div key={scanner.dashboard_id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{scanner.dashboard_name}</span>
                            {scanner.scans.length > 0 ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {scanner.scans.length} scan(s)
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400">
                                <XCircle className="h-3 w-3 mr-1" />
                                Not scanned
                              </Badge>
                            )}
                          </div>
                          {scanner.scans.length > 0 && (
                            <div className="mt-2 text-xs text-gray-500">
                              {scanner.scans.map((s, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatToLocalDateTime(s.scanned_at)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scanner-wise Tab */}
          <TabsContent value="scanner-wise">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    {selectedScanner ? selectedScanner.dashboard_name : 'All Scanners'}
                  </CardTitle>
                  {selectedScanner && (
                    <Button variant="outline" size="sm" onClick={() => setSelectedScanner(null)} data-testid="clear-scanner">
                      Back to List
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedScanner ? (
                  <>
                    {/* Scanner List with Counts */}
                    {scannerStats.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No scanners configured</p>
                    ) : (
                      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {scannerStats.map((scanner) => (
                          <div
                            key={scanner.dashboard_id}
                            className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => fetchScannerAttendees(scanner)}
                            data-testid={`scanner-item-${scanner.dashboard_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-medium">{scanner.dashboard_name}</p>
                                <p className="text-xs text-gray-500 capitalize">{scanner.dashboard_type || 'Custom'}</p>
                              </div>
                              <div className="text-right">
                                <div className="flex items-center gap-3">
                                  <div className="text-center">
                                    <p className="text-lg font-bold text-blue-600">{scanner.total_scans}</p>
                                    <p className="text-xs text-gray-500">Scans</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-lg font-bold text-green-600">{scanner.unique_attendees}</p>
                                    <p className="text-xs text-gray-500">People</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Selected Scanner Stats */}
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span>Total Scans:</span>
                        <span className="font-bold">{selectedScanner.total_scans}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Unique Attendees:</span>
                        <span className="font-bold">{selectedScanner.unique_attendees}</span>
                      </div>
                    </div>

                    {/* Attendees List */}
                    {scannerAttendees.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No attendees scanned at this location</p>
                    ) : (
                      <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                        {scannerAttendees.map((attendee) => (
                          <div key={attendee.attendee_id} className="p-3 border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{attendee.name}</p>
                                <p className="text-xs text-gray-500">{attendee.ticket_id}</p>
                              </div>
                              <Badge>{attendee.scan_count} scan(s)</Badge>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              {attendee.scan_times.slice(0, 3).map((t, i) => (
                                <span key={i} className="mr-2">{formatToLocalDateTime(t)}</span>
                              ))}
                              {attendee.scan_times.length > 3 && (
                                <span>+{attendee.scan_times.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MobileLiveDashboard;
